/**
 * ResultApprovePage — approve or dispute a submitted result at /games/:gameId/approve.
 *
 * Shows the read-only submitted result (per-game scores, games won, derived winner,
 * provisional points) plus confirmation-status chips.
 * Actions: Approve (→ completed, points) or Dispute (reason field → disputed).
 * Uses the existing approveResult / disputeResult thunks.
 */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate, Link } from 'react-router-dom';
import type { AppDispatch, RootState } from '../../store';
import { fetchGameById, approveResult, disputeResult } from '../../store/slices/gameSlice';
import { GameStatus } from '@sulis-liiga/common';
import { teamLabel, gamesWon, scoreFor } from '../../utils/gameTeams';

const ResultApprovePage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { gameId } = useParams<{ gameId: string }>();

  const { currentGame: game, loading, error } = useSelector((state: RootState) => state.game);
  const { user } = useSelector((state: RootState) => state.auth);

  const [showDisputePanel, setShowDisputePanel] = useState(false);
  const [disputeReason, setDisputeReason] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const [approving, setApproving] = useState(false);
  const [disputing, setDisputing] = useState(false);

  useEffect(() => {
    if (gameId) dispatch(fetchGameById(gameId));
  }, [dispatch, gameId]);

  const handleApprove = async () => {
    if (!gameId) return;
    setApproving(true);
    setActionError(null);
    try {
      await dispatch(approveResult(gameId)).unwrap();
      navigate(`/games/${gameId}`);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setApproving(false);
    }
  };

  const handleDispute = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!gameId || !disputeReason.trim()) return;
    setDisputing(true);
    setActionError(null);
    try {
      await dispatch(disputeResult({ gameId, reason: disputeReason })).unwrap();
      navigate(`/games/${gameId}`);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setDisputing(false);
    }
  };

  // ── Loading / Error ────────────────────────────────────────────────────────

  if (loading && !game) {
    return (
      <div className="flex items-center justify-center py-20">
        <svg
          className="animate-spin h-10 w-10 text-primary-600"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          aria-label="Loading"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      </div>
    );
  }

  if (error && !game) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="rounded-md bg-danger-50 p-4" role="alert">
          <p className="text-sm text-danger-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!game) {
    return (
      <div className="max-w-2xl mx-auto text-center py-20">
        <p className="text-gray-500">Game not found.</p>
        <Link to="/games" className="mt-3 text-sm text-primary-600 hover:underline block">
          Back to Games
        </Link>
      </div>
    );
  }

  if (!game.result) {
    return (
      <div className="max-w-2xl mx-auto">
        <Link to={`/games/${gameId}`} className="text-sm text-primary-600">
          &larr; Back to game
        </Link>
        <div className="mt-4 rounded-md bg-yellow-50 p-4">
          <p className="text-sm text-yellow-800">
            No result has been submitted for this game yet.{' '}
            <Link to={`/games/${gameId}/result`} className="underline font-medium">
              Enter a result
            </Link>
          </p>
        </div>
      </div>
    );
  }

  if (game.result.verified || game.status === GameStatus.COMPLETED) {
    return (
      <div className="max-w-2xl mx-auto">
        <Link to={`/games/${gameId}`} className="text-sm text-primary-600">
          &larr; Back to game
        </Link>
        <div className="mt-4 rounded-md bg-success-50 p-4">
          <p className="text-sm text-success-800">
            This result has already been approved. The game is <strong>Completed</strong>.
          </p>
        </div>
      </div>
    );
  }

  // ── Main view ──────────────────────────────────────────────────────────────

  const result = game.result;
  const [teamAId, teamBId] = game.teamIds;
  const teamALabel = teamLabel(game, teamAId);
  const teamBLabel = teamLabel(game, teamBId);

  // Determine per-set scores from result.sets (if available)
  const sets = result.sets ?? [];

  // Determine winner label
  const winnerLabel = teamLabel(game, result.winner);
  const retiredLabel = result.retiredTeamId ? teamLabel(game, result.retiredTeamId) : null;

  // Confirmation chips — approximated from the verification list (no home/away):
  // the result is "submitted" once one team confirms and "approved" once both do.
  const submittedConfirmed = result.verifications.some(v => v.approved);
  const bothConfirmed = result.verifications.length > 1;

  // Check if the viewer has already verified
  const myVerification = user ? result.verifications.find(v => v.userId === user.id) : undefined;
  const alreadyActed = !!myVerification;

  // Submitter label (first verification if any)
  const submitterName = result.verifications[0]?.userName;
  const submitterLabel =
    result.verifications.length > 0
      ? `Submitted ${new Date(result.verifications[0].timestamp).toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'short',
          hour: '2-digit',
          minute: '2-digit',
        })}`
      : 'Submitted';

  return (
    <div className="max-w-2xl mx-auto">
      <Link to={`/games/${gameId}`} className="text-sm text-primary-600 hover:text-primary-700">
        &larr; Back to game
      </Link>
      <h1 className="mt-2 text-3xl font-bold text-gray-900">
        {alreadyActed ? 'Result submitted' : 'Approve result'}
      </h1>
      <p className="mt-1 text-gray-600">
        {alreadyActed
          ? 'You submitted this result. It is now waiting for the other team to approve or dispute.'
          : 'A result has been submitted for this game. Review and approve, or dispute if it’s wrong.'}
      </p>

      {/* Game header */}
      <section className="mt-4 bg-white rounded-lg shadow-sm p-5">
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-500">{game.divisionName ?? 'Scheduled game'}</div>
          <span
            className="text-xs px-2 py-0.5 rounded-full"
            style={{ background: '#fef3c7', color: '#92400e' }}
          >
            Awaiting approval
          </span>
        </div>
        <div className="mt-1 text-lg font-semibold text-gray-900">
          {teamALabel} <span className="text-gray-400 font-normal">vs</span> {teamBLabel}
        </div>
        <div className="mt-1 text-sm text-gray-500">
          {game.scheduledDate
            ? new Date(game.scheduledDate).toLocaleDateString('en-GB', {
                weekday: 'short',
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              })
            : ''}
          {game.scheduledTime ? `, ${game.scheduledTime}` : ''}
          {game.venue?.name ? ` · ${game.venue.name}` : ''}
          {game.venue?.address ? `, ${game.venue.address}` : ''}
        </div>
      </section>

      {/* Submitted result (read-only) */}
      <section className="mt-4 bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-700">Submitted result</h2>
          <span className="text-xs text-gray-400">
            {submitterName ? `by ${submitterName} · ` : ''}
            {submitterLabel}
          </span>
        </div>

        {retiredLabel && (
          <div className="mt-3 rounded-md bg-amber-50 text-amber-800 text-sm px-3 py-2">
            <span className="font-semibold">Retirement</span> — {retiredLabel} retired; the score
            below is what was played up to that point. {winnerLabel} wins.
          </div>
        )}

        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-gray-500">
              <tr>
                <th className="text-left px-3 py-1 font-medium" />
                {(sets.length > 0 ? sets : [0, 1, 2]).map((_, i) => (
                  <th key={i} className="px-3 py-1 font-medium text-center">
                    G{i + 1}
                  </th>
                ))}
                <th className="px-3 py-1 font-medium text-center">Games</th>
              </tr>
            </thead>
            <tbody>
              {game.teamIds.map(teamId => {
                const isWinner = result.winner === teamId;
                return (
                  <tr
                    key={teamId}
                    className={`border-t border-gray-100 ${isWinner ? 'bg-success-50' : ''}`}
                  >
                    <td className="px-3 py-2 font-medium text-gray-900">
                      {teamLabel(game, teamId)}
                    </td>
                    {(sets.length > 0 ? sets : [null, null, null]).map((s, i) => (
                      <td key={i} className="px-3 py-2 text-center">
                        {s ? scoreFor(s, teamId) : '—'}
                      </td>
                    ))}
                    <td
                      className={`px-3 py-2 text-center font-bold ${
                        isWinner ? 'text-success-800' : 'text-gray-500'
                      }`}
                    >
                      {gamesWon(result, teamId)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <p className="mt-3 text-sm text-gray-700">
          Winner: <span className="font-semibold text-success-800">{winnerLabel}</span>
          {' · '}provisional points: {teamALabel} +{scoreFor(game.pointsAwarded, teamAId)},{' '}
          {teamBLabel} +{scoreFor(game.pointsAwarded, teamBId)}
        </p>
      </section>

      {/* Confirmation status */}
      <section className="mt-4 bg-white rounded-lg shadow-sm p-5">
        <h2 className="text-sm font-semibold text-gray-700">Confirmation status</h2>
        <div className="mt-3 flex flex-wrap gap-3 text-sm">
          <span
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${
              submittedConfirmed ? 'bg-success-100 text-success-800' : 'bg-gray-100 text-gray-600'
            }`}
          >
            {submittedConfirmed ? '✓' : '●'} Result submitted
          </span>
          <span
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${
              bothConfirmed ? 'bg-success-100 text-success-800' : ''
            }`}
            style={!bothConfirmed ? { background: '#fef3c7', color: '#92400e' } : undefined}
          >
            {bothConfirmed ? '✓' : '●'}{' '}
            {bothConfirmed
              ? 'Both teams approved'
              : alreadyActed
              ? "Awaiting the other team's approval"
              : 'Your approval needed'}
          </span>
        </div>
      </section>

      {/* Error */}
      {actionError && (
        <div className="mt-4 rounded-md bg-danger-50 p-4" role="alert">
          <p className="text-sm text-danger-600">{actionError}</p>
        </div>
      )}

      {/* Actions */}
      {!alreadyActed && (
        <div className="mt-6 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => setShowDisputePanel(p => !p)}
            className="px-4 py-2 rounded-md text-sm font-medium text-danger-600 border border-danger-600 hover:bg-danger-50 transition-colors"
            aria-expanded={showDisputePanel}
          >
            Dispute
          </button>
          <button
            type="button"
            onClick={handleApprove}
            disabled={approving}
            className="px-5 py-2.5 rounded-md text-sm font-medium text-white bg-success-600 hover:bg-success-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {approving ? 'Approving…' : 'Approve result'}
          </button>
        </div>
      )}

      {alreadyActed && (
        <div className="mt-6 rounded-md bg-primary-50 p-4 text-sm text-primary-800">
          You’ve submitted this result. The other team needs to approve (or dispute) it before
          points are awarded — there’s nothing more for you to do right now.
        </div>
      )}

      {/* Dispute panel */}
      {showDisputePanel && (
        <section
          className="mt-4 bg-white rounded-lg shadow-sm p-6"
          style={{ borderWidth: 1, borderStyle: 'solid', borderColor: '#fee2e2' }}
        >
          <h2 className="text-sm font-semibold" style={{ color: '#991b1b' }}>
            Dispute this result
          </h2>
          <p className="mt-1 text-xs text-gray-500">
            Explain what&apos;s wrong. The game is flagged <strong>Disputed</strong> and a league
            administrator will resolve it.
          </p>
          <form onSubmit={handleDispute} noValidate>
            <label
              htmlFor="dispute-reason"
              className="block text-sm font-medium text-gray-700 mt-3"
            >
              Reason
            </label>
            <textarea
              id="dispute-reason"
              rows={3}
              value={disputeReason}
              onChange={e => setDisputeReason(e.target.value)}
              placeholder="e.g. Game 2 score should be 21–17, not 19–21."
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              required
              aria-invalid={!disputeReason.trim() && showDisputePanel}
            />
            <div className="mt-3 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowDisputePanel(false)}
                className="px-4 py-2 rounded-md text-sm font-medium text-gray-600 border border-gray-300 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={disputing || !disputeReason.trim()}
                className="px-4 py-2 rounded-md text-sm font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                style={{ background: '#dc2626' }}
              >
                {disputing ? 'Submitting…' : 'Submit dispute'}
              </button>
            </div>
          </form>
        </section>
      )}

      {/* Info */}
      <div className="mt-4 rounded-md bg-primary-50 text-primary-800 text-sm p-3">
        {alreadyActed ? (
          <>
            The other team will <strong>approve or dispute</strong> this result. Points are awarded
            once they approve; a dispute pauses it for a league administrator to resolve.
          </>
        ) : (
          <>
            Once you approve, the game is marked <strong>Completed</strong> and points are awarded
            to the standings. Disputes pause the result until an admin resolves them.
          </>
        )}
      </div>
    </div>
  );
};

export default ResultApprovePage;
